import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useCart } from '../context/CartContext';
import { ordersAPI, getImageUrl } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorHandler';
import { ShoppingBag, Package, User, Mail, Phone, MapPin, FileText, ArrowRight, Check } from 'lucide-react';

const DELIVERY_FEE = 1.000;

const steps = ['Cart', 'Delivery', 'Confirm'];

const InputField = ({ icon: Icon, label, type = 'text', placeholder, value, onChange, required }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}{required && ' *'}</label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />}
      <input
        type={type}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#8acb95] focus:ring-2 focus:ring-[#8acb95]/10 bg-white transition-all`}
      />
    </div>
  </div>
);

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart, getItemPrice } = useCart();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    notes: '',
  });

  const set = (key) => (e) => setFormData((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const orderData = {
        ...formData,
        items: cart.map((item) => ({
          product_id: item.id,
          product_name: item.name,
          product_code: item.code,
          quantity: item.quantity,
          price: item.price,
          discount_rate: item.discount_rate || 0,
          final_price: getItemPrice(item),
        })),
      };
      const response = await ordersAPI.create(orderData);
      clearCart();
      toast.success('Order placed successfully!');
      navigate(`/order-success/${response.data.id}`);
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Navbar />

      <div className="container mx-auto px-4 md:px-8 max-w-6xl py-10 flex-1">

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-0 mb-10 max-w-xs mx-auto">
          {steps.map((step, i) => (
            <div key={step} className="flex items-center">
              <div className={`flex items-center gap-2 ${i === 1 ? 'text-[#8acb95]' : 'text-slate-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                  i === 0
                    ? 'bg-slate-100 border-slate-200 text-slate-400'
                    : i === 1
                    ? 'bg-[#8acb95] border-[#8acb95] text-white'
                    : 'bg-white border-slate-200 text-slate-400'
                }`}>
                  {i === 0 ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </div>
                <span className="text-xs font-medium hidden sm:block">{step}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`w-12 h-0.5 mx-2 ${i < 1 ? 'bg-[#8acb95]' : 'bg-slate-200'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Delivery Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#8acb95]" />
                </div>
                <div>
                  <h2 className="font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Delivery Information
                  </h2>
                  <p className="text-xs text-slate-400">Fill in your delivery details</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    icon={User}
                    label="Full Name"
                    placeholder="Ahmed Al-Rashid"
                    value={formData.customer_name}
                    onChange={set('customer_name')}
                    required
                  />
                  <InputField
                    icon={Phone}
                    label="Phone Number"
                    placeholder="+965 XXXXXXXX"
                    value={formData.customer_phone}
                    onChange={set('customer_phone')}
                    required
                  />
                </div>
                <InputField
                  icon={Mail}
                  label="Email Address"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.customer_email}
                  onChange={set('customer_email')}
                  required
                />
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Delivery Address *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400" />
                    <textarea
                      required
                      placeholder="Block, Street, House/Flat No., Area, City"
                      rows={3}
                      value={formData.customer_address}
                      onChange={set('customer_address')}
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#8acb95] focus:ring-2 focus:ring-[#8acb95]/10 bg-white transition-all resize-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Order Notes (Optional)
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400" />
                    <textarea
                      placeholder="Special delivery instructions..."
                      rows={2}
                      value={formData.notes}
                      onChange={set('notes')}
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#8acb95] focus:ring-2 focus:ring-[#8acb95]/10 bg-white transition-all resize-none"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2.5 bg-[#8acb95] hover:bg-[#7bb885] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold py-4 rounded-2xl transition-all shadow-sm hover:shadow-md text-sm mt-2"
                >
                  {loading ? (
                    <>Placing Order...</>
                  ) : (
                    <>
                      <ShoppingBag className="w-5 h-5" />
                      Place Order · Cash on Delivery
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 sticky top-28">
              <h2 className="font-bold text-slate-800 mb-5 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Package className="w-4 h-4 text-[#8acb95]" />
                Order Summary
              </h2>

              <div className="space-y-3 mb-4 max-h-60 overflow-y-auto pr-1">
                {cart.map((item) => (
                  <div key={item.id} className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-slate-50 rounded-lg overflow-hidden flex-shrink-0 border border-slate-100">
                      {item.images && item.images.length > 0 ? (
                        <img src={getImageUrl(item.images[0])} alt={item.name} className="w-full h-full object-contain p-1" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-5 h-5 text-slate-200" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700 line-clamp-2 leading-snug">{item.name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">× {item.quantity}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {item.discount_rate > 0 ? (
                        <p className="text-xs font-bold text-red-600">
                          KWD {(getItemPrice(item) * item.quantity).toFixed(3)}
                        </p>
                      ) : (
                        <p className="text-xs font-bold text-slate-700">
                          KWD {(item.price * item.quantity).toFixed(3)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-slate-100 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="font-medium">KWD {getCartTotal().toFixed(3)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Delivery Fee</span>
                  <span className="font-medium text-slate-700">KWD {DELIVERY_FEE.toFixed(3)}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                  <span className="font-bold text-slate-800">Total</span>
                  <span className="text-xl font-bold text-[#8acb95]">KWD {(getCartTotal() + DELIVERY_FEE).toFixed(3)}</span>
                </div>
                <p className="text-xs text-slate-400 pt-1">💳 Payment: Cash on Delivery (COD)</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
